import * as THREE from "three";
import { WORLD } from "../config";
import { makeBerthTexture, type ClubMaterials } from "../materials";
import { BERTHS, FLEET } from "../boats";
import { EAST_PONTOON, SOUTH_QUAY, WEST_PONTOON } from "../habitat/places";

function box(
  w: number,
  h: number,
  d: number,
  x: number,
  y: number,
  z: number,
  mat: THREE.Material,
  rotY = 0,
): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  m.position.set(x, y, z);
  m.rotation.y = rotY;
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

function strip(
  points: Array<[number, number]>,
  width: number,
  y: number,
  thick: number,
  mat: THREE.Material,
): THREE.Group {
  const g = new THREE.Group();
  for (let i = 0; i < points.length - 1; i++) {
    const [x0, z0] = points[i];
    const [x1, z1] = points[i + 1];
    const dx = x1 - x0;
    const dz = z1 - z0;
    const len = Math.hypot(dx, dz);
    const ang = Math.atan2(dx, dz);
    g.add(box(width, thick, len + 0.15, (x0 + x1) * 0.5, y, (z0 + z1) * 0.5, mat, ang));
  }
  return g;
}

export function buildDocks(mats: ClubMaterials): THREE.Group {
  const g = new THREE.Group();
  g.name = "docks";
  const top = WORLD.quayHeight;
  const wall = top - WORLD.seabed;
  const cy = WORLD.seabed + wall * 0.5;

  g.add(strip(SOUTH_QUAY, 7.0, cy, wall, mats.concrete));
  g.add(strip(SOUTH_QUAY, 7.0, top + 0.04, 0.08, mats.whiteConcrete));
  g.add(strip(WEST_PONTOON, 2.2, top - 0.12, 0.34, mats.darkConcrete));
  g.add(strip(EAST_PONTOON, 2.2, top - 0.12, 0.34, mats.darkConcrete));

  g.add(ladder(-10, -29.2, mats));
  g.add(ladder(14, -29.4, mats));

  for (let i = 0; i < BERTHS.length; i++) {
    const berth = BERTHS[i];
    const spec = FLEET[i];
    if (berth.pierSide === 0) {
      g.add(mooring(berth.x, berth.z, mats));
      continue;
    }
    const heading = berth.heading;
    const c = Math.cos(heading);
    const s = Math.sin(heading);
    const side = berth.pierSide;
    const dist = spec.beam * 0.5 + 2.4;
    const fingerLen = 9;
    const fx = berth.x + side * (dist + fingerLen * 0.3) * c;
    const fz = berth.z - side * (dist + fingerLen * 0.3) * s;
    g.add(box(1.55, 0.3, fingerLen, fx, top - 0.12, fz, mats.darkConcrete, heading + Math.PI / 2));

    const pileH = top + 0.85 - WORLD.seabed;
    for (const end of [-0.35, 0.35] as const) {
      const pile = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.24, pileH, 10), mats.darkConcrete);
      pile.position.set(
        berth.x + side * 5.2 * c + end * spec.length * 0.28 * s,
        WORLD.seabed + pileH * 0.5,
        berth.z - side * 5.2 * s + end * spec.length * 0.28 * c,
      );
      pile.castShadow = true;
      g.add(pile);
    }

    const board = new THREE.Mesh(
      new THREE.PlaneGeometry(1.1, 0.5),
      new THREE.MeshStandardMaterial({ map: makeBerthTexture(berth.id), roughness: 0.5 }),
    );
    const signX = berth.x + side * 7.4 * c;
    const signZ = berth.z - side * 7.4 * s;
    board.position.set(signX, top + 1.1, signZ);
    board.rotation.y = heading + (side < 0 ? Math.PI / 2 : -Math.PI / 2);
    g.add(board);

    for (const along of [-3.2, 0, 3.2]) {
      const f = new THREE.Mesh(new THREE.SphereGeometry(0.2, 8, 6), mats.coral);
      f.position.set(
        berth.x + side * (spec.beam * 0.5 + 0.55) * c + along * s,
        0.32,
        berth.z - side * (spec.beam * 0.5 + 0.55) * s + along * c,
      );
      g.add(f);
    }
  }

  g.add(channelMarkers(mats));
  g.add(lightPoles(mats));
  return g;
}

function ladder(x: number, z: number, mats: ClubMaterials): THREE.Group {
  const g = new THREE.Group();
  for (let i = 0; i < 8; i++) {
    const r = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.7, 6), mats.chrome);
    r.rotation.x = Math.PI / 2;
    r.position.set(x, WORLD.seabed + 0.4 + i * 0.42, z);
    g.add(r);
  }
  return g;
}

function mooring(x: number, z: number, mats: ClubMaterials): THREE.Group {
  const g = new THREE.Group();
  const ball = new THREE.Mesh(new THREE.SphereGeometry(0.38, 10, 8), mats.coral);
  ball.position.set(x, 0.28, z);
  g.add(ball);
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.7, 6), mats.steel);
  pole.position.set(x, 0.15, z);
  g.add(pole);
  return g;
}

function channelMarkers(mats: ClubMaterials): THREE.Group {
  const g = new THREE.Group();
  const green = new THREE.MeshStandardMaterial({ color: 0x2f9e44, roughness: 0.45 });
  const marks: Array<[number, number, THREE.MeshStandardMaterial]> = [
    [-4, 74, mats.coral],
    [34, 72, green],
  ];
  for (const [x, z, mat] of marks) {
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.14, 4.2, 8), mats.steel);
    pole.position.set(x, 1.1, z);
    pole.castShadow = true;
    g.add(pole);
    const top = new THREE.Mesh(new THREE.ConeGeometry(0.38, 0.7, 8), mat);
    top.position.set(x, 3.4, z);
    g.add(top);
  }
  return g;
}

function lightPoles(mats: ClubMaterials): THREE.Group {
  const g = new THREE.Group();
  const spots: Array<[number, number, number]> = [
    [-22, -24, 0],
    [6, -32, 0],
    [28, -20, 0],
    [-38, 6, 1],
    [36, 0, -1],
    [-32, 28, 1],
  ];
  for (const [x, z, inward] of spots) {
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.09, 4.6, 8), mats.steel);
    pole.position.set(x, WORLD.quayHeight + 2.3, z);
    pole.castShadow = true;
    g.add(pole);
    const arm = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.06, 0.06), mats.steel);
    arm.position.set(x + inward * 0.6, WORLD.quayHeight + 4.5, z);
    g.add(arm);
    const lamp = new THREE.Mesh(new THREE.SphereGeometry(0.14, 10, 8), mats.white);
    lamp.position.set(x + inward * 1.15, WORLD.quayHeight + 4.35, z);
    g.add(lamp);
  }
  return g;
}
